import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { useApiQuery } from '@/hooks/use-api'
import { i18n } from '@/lib/i18n'
import type { BadgeProps } from '@/components/ui/badge'
import type { TemplateSpec, ZnsTemplateDesign } from '@/pages/zns/zns-template-form'

// ── Trạng thái chiến dịch ZNS ────────────────────────────────────────
export type ZnsCampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'queued'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled'

export const CAMPAIGN_STATUS_LABELS: Record<string, string> = {
  get draft() { return i18n.t('marketing:campaignStatus.draft') },
  get scheduled() { return i18n.t('marketing:campaignStatus.scheduled') },
  get queued() { return i18n.t('marketing:campaignStatus.queued') },
  get running() { return i18n.t('marketing:campaignStatus.running') },
  get paused() { return i18n.t('marketing:campaignStatus.paused') },
  get completed() { return i18n.t('marketing:campaignStatus.completed') },
  get failed() { return i18n.t('marketing:campaignStatus.failed') },
  get cancelled() { return i18n.t('marketing:campaignStatus.cancelled') },
}

export function campaignStatusLabel(status?: string | null): string {
  if (!status) return '—'
  return CAMPAIGN_STATUS_LABELS[status] ?? status
}

export function campaignStatusVariant(status?: string | null): BadgeProps['variant'] {
  switch (status) {
    case 'completed':
      return 'success'
    case 'running':
      return 'default'
    case 'queued':
    case 'scheduled':
    case 'paused':
      return 'warning'
    case 'failed':
      return 'destructive'
    case 'cancelled':
      return 'secondary'
    default:
      return 'secondary'
  }
}

// ── Trạng thái người nhận ────────────────────────────────────────────
export const RECIPIENT_STATUS_LABELS: Record<string, string> = {
  get pending() { return i18n.t('marketing:recipientStatus.pending') },
  get sending() { return i18n.t('marketing:recipientStatus.sending') },
  get sent() { return i18n.t('marketing:recipientStatus.sent') },
  get failed() { return i18n.t('marketing:recipientStatus.failed') },
  get skipped() { return i18n.t('marketing:recipientStatus.skipped') },
}

export function recipientStatusLabel(status?: string | null): string {
  if (!status) return '—'
  return RECIPIENT_STATUS_LABELS[status] ?? status
}

export function recipientStatusVariant(status?: string | null): BadgeProps['variant'] {
  switch (status) {
    case 'sent':
      return 'success'
    case 'failed':
      return 'destructive'
    case 'skipped':
      return 'warning'
    default:
      return 'secondary'
  }
}

// ── Kiểu dữ liệu ─────────────────────────────────────────────────────
/** Tài khoản Zalo OA — dùng để chọn nguồn gửi ZNS. */
export interface OaAccount {
  id: string
  platform: string
  displayName: string | null
  avatarUrl: string | null
  externalPageId: string | null
  status: string | null
  liveStatus?: string | null
}

/** Tham số của một mẫu ZNS (theo cấu trúc Zalo). */
export interface ZnsTemplateParam {
  name: string
  require?: boolean
  type?: string
  maxLength?: number
  minLength?: number
  acceptNull?: boolean
}

/** Mẫu ZNS (chỉ đọc, đồng bộ từ Zalo). */
export interface ZnsTemplate {
  id: string
  channelAccountId: string
  templateId: string
  templateName: string
  status: string // ENABLE | DISABLE | PENDING_REVIEW
  templateType: string | null
  params: ZnsTemplateParam[]
  previewUrl: string | null
  fetchedAt: string
  /** TRANSACTION | CUSTOMER_CARE | PROMOTION */
  tag?: string | null
  /** Mô hình soạn (chỉ có với mẫu tạo từ eCDP) — dùng để sửa lại mẫu bị từ chối. */
  layout?: ZnsTemplateDesign | null
  rejectReason?: string | null
  quality?: string | null
  priceSdt?: string | null
  priceUid?: string | null
  statusUpdatedAt?: string | null
}

export interface ZnsCampaign {
  id: string
  orgId: string
  channelAccountId: string
  name: string
  templateId: string
  templateData: Record<string, string>
  contactIds: string[]
  segmentId?: string | null
  windowStart?: number | null
  windowEnd?: number | null
  pauseReason?: string | null
  status: ZnsCampaignStatus
  scheduledAt: string | null
  startedAt: string | null
  completedAt: string | null
  totalCount: number
  sentCount: number
  failedCount: number
  skippedCount: number
  createdByUserId: string
  createdAt: string
  channelAccount?: { id: string; displayName: string | null; externalPageId: string | null } | null
  createdBy?: { id: string; fullName: string | null } | null
}

export interface ZnsCampaignRecipient {
  id: string
  contactId: string | null
  phone: string
  status: string
  errorCode?: string | null
  errorMessage: string | null
  processedAt: string | null
}

export interface ZnsCampaignDetail extends ZnsCampaign {
  recipients: ZnsCampaignRecipient[]
}

export interface CreateCampaignInput {
  name: string
  channelAccountId: string
  templateId: string
  templateData: Record<string, string>
  contactIds?: string[]
  segmentId?: string | null
  scheduledAt?: string | null
  windowStart?: number | null
  windowEnd?: number | null
}

export interface CampaignPreview {
  total: number
  sendable: number
  skipped: number
  templateStatus: string | null
  tag: string | null
  unitPrice: number | null
  estimatedCost: number | null
  quotaRemaining: number | null
  quotaFetchedAt: string | null
  exceedsQuota: boolean
}

// ── Query hooks ──────────────────────────────────────────────────────
/** Danh sách tài khoản Zalo OA (nguồn gửi ZNS). */
export function useOaAccounts() {
  return useApiQuery<OaAccount[]>(['zalo-accounts', { type: 'oa' }], '/zalo-accounts', {
    type: 'oa',
  })
}

/** Danh sách mẫu ZNS của một tài khoản OA. Mặc định chỉ mẫu đã duyệt (ENABLE) để gửi; `all` = mọi trạng thái. */
export function useZnsTemplates(accountId: string | undefined, refresh?: boolean, all?: boolean) {
  return useQuery<ZnsTemplate[]>({
    queryKey: ['zns-templates', { accountId, refresh: refresh ? 1 : 0, all: all ? 1 : 0 }],
    queryFn: async () => {
      const { data } = await api.get<ZnsTemplate[]>('/zns/templates', {
        params: { accountId, ...(refresh ? { refresh: '1' } : {}), ...(all ? { all: '1' } : {}) },
      })
      return data
    },
    enabled: !!accountId,
  })
}

// ── Quản lý mẫu tin ZBS ───────────────────────────────────────────────
export const TEMPLATE_STATUS_LABELS: Record<string, string> = {
  get ENABLE() { return i18n.t('marketing:templateStatus.ENABLE') },
  get PENDING_REVIEW() { return i18n.t('marketing:templateStatus.PENDING_REVIEW') },
  get REJECT() { return i18n.t('marketing:templateStatus.REJECT') },
  get DISABLE() { return i18n.t('marketing:templateStatus.DISABLE') },
  get DELETE() { return i18n.t('marketing:templateStatus.DELETE') },
}

export function templateStatusVariant(status?: string | null): BadgeProps['variant'] {
  switch (status) {
    case 'ENABLE':
      return 'success'
    case 'PENDING_REVIEW':
      return 'warning'
    case 'REJECT':
      return 'destructive'
    default:
      return 'secondary'
  }
}

export const TEMPLATE_QUALITY_LABELS: Record<string, string> = {
  get HIGH() { return i18n.t('marketing:templateQuality.HIGH') },
  get MEDIUM() { return i18n.t('marketing:templateQuality.MEDIUM') },
  get LOW() { return i18n.t('marketing:templateQuality.LOW') },
  get UNDEFINED() { return i18n.t('marketing:templateQuality.UNDEFINED') },
}

export const TEMPLATE_TAG_LABELS: Record<string, string> = {
  get TRANSACTION() { return i18n.t('marketing:templateTag.TRANSACTION') },
  get CUSTOMER_CARE() { return i18n.t('marketing:templateTag.CUSTOMER_CARE') },
  get PROMOTION() { return i18n.t('marketing:templateTag.PROMOTION') },
}

/** Thông số soạn mẫu (loại mẫu, tag, tham số, nút bấm…) do backend cung cấp — một nguồn sự thật. */
export function useTemplateSpec() {
  return useQuery<TemplateSpec>({
    queryKey: ['zns-template-spec'],
    queryFn: async () => (await api.get<TemplateSpec>('/zns/template-spec')).data,
    staleTime: 60 * 60 * 1000,
  })
}

export interface TemplateDesignCheck { errors: string[]; warnings: string[] }

/** Kiểm cấu trúc + quét theo quy định kiểm duyệt ZBS của Zalo (zns-template-policy.ts, backend) — `errors` chặn
 * gửi, `warnings` chỉ gợi ý xem lại (vd ngành cần giấy phép, thiếu voucher khi nhắc khuyến mãi…). */
export async function validateTemplateDesign(design: ZnsTemplateDesign): Promise<TemplateDesignCheck> {
  const { data } = await api.post<{ errors: string[]; warnings?: string[] }>('/zns/templates/validate', { design })
  return { errors: data.errors, warnings: data.warnings ?? [] }
}

/** Tải logo / ảnh lên Zalo, trả media_id. Backend kiểm tra kích thước, định dạng trước khi gửi. */
export async function uploadTemplateImage(accountId: string, kind: 'logo' | 'image', file: File): Promise<string> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post<{ mediaId: string }>('/zns/templates/images', form, {
    params: { accountId, kind },
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data.mediaId
}

export function useSubmitZnsTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { accountId: string; design: ZnsTemplateDesign; resubmitTemplateId?: string }) => {
      const { accountId, design, resubmitTemplateId } = input
      const { data } = resubmitTemplateId
        ? await api.put<ZnsTemplate>(`/zns/templates/${encodeURIComponent(resubmitTemplateId)}`, { accountId, design })
        : await api.post<ZnsTemplate>('/zns/templates', { accountId, design })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zns-templates'] }),
  })
}

export function useSyncZnsTemplates() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (accountId: string) => {
      const { data } = await api.post<{ total: number; detailFetched: number; removed: number }>('/zns/templates/sync', { accountId })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zns-templates'] }),
  })
}

/** Danh sách chiến dịch ZNS. */
export function useZnsCampaigns(params?: { status?: string; limit?: number }) {
  return useApiQuery<ZnsCampaign[]>(
    ['zns-campaigns', params ?? {}],
    '/zns/campaigns',
    params as Record<string, unknown> | undefined,
    { placeholderData: (prev) => prev },
  )
}

/** Chi tiết một chiến dịch + danh sách người nhận. Tự động refetch khi đang chạy. */
export function useZnsCampaign(id: string | undefined, recipientLimit = 200) {
  return useQuery<ZnsCampaignDetail>({
    queryKey: ['zns-campaign', id, { recipientLimit }],
    queryFn: async () => {
      const { data } = await api.get<ZnsCampaignDetail>(`/zns/campaigns/${id}`, {
        params: { recipientLimit },
      })
      return data
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const s = query.state.data?.status
      return s === 'running' || s === 'queued' ? 4000 : false
    },
  })
}

// ── Mutation hooks ───────────────────────────────────────────────────
export function useCreateZnsCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateCampaignInput) => {
      const { data } = await api.post<ZnsCampaign>('/zns/campaigns', input)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zns-campaigns'] }),
  })
}

export function useStartZnsCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/zns/campaigns/${id}/start`)
      return data as { ok: boolean; enqueued: number; skipped: number }
    },
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['zns-campaigns'] })
      qc.invalidateQueries({ queryKey: ['zns-campaign', id] })
    },
  })
}

export function useCancelZnsCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/zns/campaigns/${id}/cancel`)
      return data as { ok: boolean }
    },
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['zns-campaigns'] })
      qc.invalidateQueries({ queryKey: ['zns-campaign', id] })
    },
  })
}

export function usePreviewZnsCampaign() {
  return useMutation({
    mutationFn: async (input: {
      channelAccountId: string
      templateId: string
      contactIds?: string[]
      segmentId?: string | null
    }) => (await api.post<CampaignPreview>('/zns/campaigns/preview', input)).data,
  })
}

export function useTestSendZns() {
  return useMutation({
    mutationFn: async (input: {
      channelAccountId: string
      templateId: string
      templateData: Record<string, string>
      phone: string
    }) => (await api.post<{ ok: boolean }>('/zns/campaigns/test-send', input)).data,
  })
}

function useCampaignAction(action: 'pause' | 'resume') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await api.post(`/zns/campaigns/${id}/${action}`)).data,
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['zns-campaigns'] })
      qc.invalidateQueries({ queryKey: ['zns-campaign', id] })
    },
  })
}
export const usePauseZnsCampaign = () => useCampaignAction('pause')
export const useResumeZnsCampaign = () => useCampaignAction('resume')

export interface ZnsReport {
  accepted: number
  delivered: number
  read: number
  failed: number
  awaitingDelivery: number
  deliveryRate: number | null
  unitPrice: number | null
  estimatedCost: number | null
  errors: Array<{ code: string; count: number; kind: string; message: string }>
}

export function useZnsCampaignReport(id: string | undefined, running: boolean) {
  return useQuery<ZnsReport>({
    queryKey: ['zns-campaign-report', id],
    queryFn: async () => (await api.get<ZnsReport>(`/zns/campaigns/${id}/report`)).data,
    enabled: !!id,
    refetchInterval: running ? 10_000 : false,
  })
}

export function useReconcileZnsCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ candidates: number; checked: number; nowDelivered: number }>(`/zns/campaigns/${id}/reconcile`)).data,
    onSuccess: (_d, id) => qc.invalidateQueries({ queryKey: ['zns-campaign-report', id] }),
  })
}
